import type { Ticker } from 'pixi.js';
import type { TimelineSettings } from './BaseTimeline';
import { ConcurrentTimeline } from './ConcurrentTimeline';

/**
 * TickerTimeline uses the Ticker's update mechanism to evaluate the current time. It attaches play, pause and stop to
 * the ticker's start and stop functionality.
 *
 * play() is asynchronous and will cause the timeline to start playing on the next tick/update.
 * pause() and stop() are synchronous and will cause the timeline to stop playback immediately.
 */
export class TickerTimeline extends ConcurrentTimeline {
    constructor(ticker: Ticker, settings: TimelineSettings = {}) {
        super(settings);

        this.name = 'TickerTimeline';
        this._ticker = null;
        this._playing = false;
        this._playRequested = false;
        this._startPlayAtTime = 0;

        this.#initTicker(ticker);
    }

    /**
     * _initTicker hooks up the given PIXI Ticker to be controlled by our Timeline
     */
    #initTicker(ticker: Ticker) {
        if (!ticker) {
            throw Error('PIXI.Ticker object is missing.');
        }
        this._ticker = ticker;

        // Attach our update method to the tickers update method. For each tick we're getting an update resulting in an
        // updated currentTime time.
        this._ticker.add(this._tickerUpdate, this);
    }

    /**
     * Reset the timeline.
     */
    reset() {
        this.currentTime = 0;
        this.#clearRequests();
    }

    /**
     * Start playing the timeline from the current time
     */
    override play() {
        this.#clearRequests();
        this._playRequested = true;
        this._startPlayAtTime = this.currentTime;
    }

    /**
     * Pauses the playback
     */
    pause() {
        this._playing = false;
        this.emit('pause');
    }

    /**
     * Stop playing the timeline and reset.
     */
    stop() {
        // Since stop resets the currentTime to zero, we don't need to sync it to the ticker
        this.pause();

        // NOTE: each time we use currentTime we get an update event. Since stop resets the head it sets currentTime to 0 and thus
        // we get one more update event call. The main motivation of moving the head back to 0 during stop instead of
        // play is that it removes the need to actually seek to 0 to start playback which can cause a delay when
        // dealing with video.
        this.reset();
    }

    /**
     * Returns True is the timeline is currently playing
     */
    override playing() {
        return this._playing || this._playRequested;
    }

    /**
     * tick _play method -- is hooked up the ticker _update event.
     * @internal
     */
    _play() {
        this._playing = true;
        this.#clearRequests();
    }

    /**
     * tick _clearRequests method -- is hooked up the ticker _play event which in turn is hooked up to the
     * _update method.
     */
    #clearRequests() {
        this._playRequested = false;
    }

    /** tick _update method -- is hooked up to the ticker update event. Each time the ticker
     * updates, this method gets called
     *
     * Using request [play] synchronizes the playing state with the ticker
     * updates, and allows the first tick to run at `currentTime` and then the second and
     * higher ticks will update the `currentTime`.
     */
    _tickerUpdate() {
        if (this._playing) {
            // The deltaMS is calculated based on the time elapsed between ticks
            // and is scaled by the ticker speed. So if 100ms passed between
            // ticks, but the ticker speed is 0.5 then the deltaMS would report 50ms.
            // This value is capped based on the minFPS. If minFPS is zero then there is no
            // cap, otherwise it will only jump by at most 1000/minFPS
            this.currentTime += this._ticker.deltaMS;
        }
        else if (this._playRequested) {
            this._play();
            this.currentTime = this._startPlayAtTime;
        }
    }
}
